<template>
  <main class="white">
    <v-layout row fill-height class="ma-0">
      <v-col style="height:100vh" xs6 class="px-5">
        <v-layout column justify-center fill-height>
          <v-flex xs3 class="pl-5 pt-3">
            <v-img
              :src="brand.logo"
              contain
              width="80"
              height="80"
              position="left"
              aspect-ratio="2.304"
            ></v-img>
          </v-flex>

          <v-flex xs7 class="px-5">
            <div class="mb-5">
              <h1 class="display">
                <span>{{ brand.name }}</span>
              </h1>
              <h4 class="mb-3" style="color: #9B9B9B">
                {{ $t('loginMessage') }}
              </h4>
            </div>

            <v-flex class="pr-3 mb-4">
              <v-text-field
                v-model="username"
                @keydown.enter="loginHandler"
                autocomplete="off"
                :label="$t('username')"
                solo
                flat
                autofocus
                type="text"
                background-color="#F6F8FA"
                class="mb-1 pb-0"
                hide-details
                name="userName"
              ></v-text-field>
              <!-- <span
                @click="forgot('username')"
                class="subtitle-2 info--text cursor-pointer"
                >{{ $t('forgotUsername') }}</span
              > -->
            </v-flex>

            <v-flex class="pr-3">
              <v-text-field
                v-model="password"
                @keydown.enter="loginHandler"
                autocomplete="off"
                :label="$t('password')"
                solo
                flat
                type="password"
                background-color="#F6F8FA"
                class="mt-10 pb-0"
                hide-details
                name="password"
              ></v-text-field>

              <forgot-password-dialog
                :show="showForgotDialog"
                :brand="brand"
                :loading="forgotLoading"
                @forgot-pass-handler="forgotPasswordHandler"
                @close="showForgotDialog = false"
              >
                <template slot="forgot-password">
                  <span
                    @click="showForgotDialog = true"
                    class="subtitle-2 info--text cursor-pointer brand--text"
                    >{{ $t('forgotPassword') }}</span
                  >
                </template>
              </forgot-password-dialog>
            </v-flex>

            <v-tooltip top color="black">
              <template v-slot:activator="{ on }">
                <div v-on="on" style="display: inline-block">
                  <v-checkbox
                    @change="e => toggleRememberMe(e)"
                    :value="requestedRememberMePreviously"
                    :input-value="requestedRememberMePreviously"
                    :label="$t('rememberMe')"
                    hide-details
                    color="brand"
                  ></v-checkbox>
                </div>
              </template>
              <span>{{ $t('rememberMeDisclaimer') }} </span>
            </v-tooltip>

            <v-layout justify-end class="mt-2">
              <v-btn
                @click="loginHandler"
                :loading="loading"
                :disabled="loading"
                large
                color="brand"
                class="white--text"
                >{{ $t('login') }}</v-btn
              >
            </v-layout>
          </v-flex>

          <v-flex xs2>
            <v-layout v-if="displaySignUpLink" row class="pt-4">
              <div class="ma-auto">
                <a href="/signup" class="mt-4 brand--text">
                  {{ $t('dontHaveAccount') }}
                </a>
              </div>
            </v-layout>
          </v-flex>
          <v-divider></v-divider>
          <v-row
            no-gutters
            class="ma-0 mt-1"
            justify="space-around"
            align="center"
          >
            <v-col cols="auto">
              <a
                :href="privacyPolicyURL[defaultLanguage]"
                :title="$t('privacyPolicy')"
                target="_blank"
                ><span style="font-size: .75rem">{{
                  $t('privacyPolicy')
                }}</span></a
              ></v-col
            >
            <v-col cols="auto">
              <a
                :href="cookiePolicyURL[defaultLanguage]"
                :title="$t('cookiePolicy')"
                target="_blank"
                ><span style="font-size: .75rem">{{
                  $t('cookiePolicy')
                }}</span></a
              >
            </v-col>
            <v-col cols="auto"
              ><span style="font-size: .75rem">{{ version }}</span></v-col
            >
          </v-row>
        </v-layout>
      </v-col>

      <v-flex class="splash-img" xs6>
        <v-img
          src="https://images.unsplash.com/photo-1542365775-f7a1efc18f6a?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=800&q=60"
          height="100vh"
          width="100%"
          position="top center"
        ></v-img>
      </v-flex>
    </v-layout>

    <!-- Display when account has been blocked -->
    <base-message-dialog
      v-if="displayMessageDialog"
      :show="displayMessageDialog"
      :setHalfWidth="true"
    >
      <template v-slot:card-image>
        <secure-login width="200px"></secure-login>
      </template>

      <template v-slot:title>
        <h3 class="mt-2">{{ $t(`${blockedAccountTitle}`) }}</h3>
      </template>

      <template v-slot:message>
        <p v-if="!accountNotFullyRegistered" class="mt-2">
          {{ $t('blockedAccountMsg') }} <br />
          {{ $t('forYourSecurity') }}
        </p>

        <p v-else class="mt-2">
          {{ $t('yourAccountHasNotBeenCompletedMsg') }}
        </p>

        <v-text-field
          autocomplete="off"
          v-model="usernameForgotEmail"
          @input="checkEmailRules"
          :label="$t('email')"
          solo
          flat
          background-color="inputBg"
          class="mt-2 pb-0"
          hide-details
          name="email"
        ></v-text-field>
      </template>

      <template v-slot:card-action>
        <v-btn
          @click="sendAppropriateEmailHandler(usernameForgotEmail)"
          color="brand"
          class="white--text"
          :disabled="!validEmail"
          >{{ $t('sendEmail') }}</v-btn
        >
      </template>
    </base-message-dialog>

    <!-- Display when account has not been completely registered -->
    <base-message-dialog
      v-if="displayAccountNotRegisteredDialog"
      :show="displayAccountNotRegisteredDialog"
      :setHalfWidth="true"
    >
      <template v-slot:card-image>
        <secure-login width="200px"></secure-login>
      </template>

      <template v-slot:title>
        <h3 class="mt-2">{{ $t('accountNotCompletelyRegistered') }}</h3>
      </template>

      <template v-slot:message>
        <p class="mt-2">
          {{ $t('incompleteRegistrationCheckEmail') }} "{{ brandName }} -
          {{ $t('confirmRegistration') }}"
        </p>
      </template>

      <template v-slot:card-action>
        <v-btn
          @click="displayAccountNotRegisteredDialog = false"
          small
          depressed
          >{{ $t('understood') }}</v-btn
        >
      </template>
    </base-message-dialog>

    <!-- Display when account has 3 or less attempts to log in -->
    <base-message-dialog
      v-if="display3attemptsDialog"
      :show="display3attemptsDialog"
      :setHalfWidth="true"
    >
      <template v-slot:card-image>
        <warning width="200px" :brand="brand"></warning>
      </template>

      <template v-slot:title>
        <h3 class="mt-2">
          {{ `${$t('numberOfAttemptsLeft')}: ${attemptsLeft}` }}
        </h3>
      </template>

      <template v-slot:message>
        <p class="mt-2">
          {{ $t('ifYouForgotPasswordMsg') }}
        </p>
      </template>

      <template v-slot:card-action>
        <v-btn @click="display3attemptsDialog = false" depressed>{{
          $t('understood')
        }}</v-btn>
      </template>
    </base-message-dialog>
  </main>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Swal from 'sweetalert2';
import { account } from '@/utilities/api';

const ForgotPasswordDialog = () =>
  import('@/components/ForgotPasswordDialog.vue');
const BaseMessageDialog = () =>
  import(
    '@/components/base_components/BaseMessageDialog/BaseMessageDialog.vue'
  );
const Warning = () => import('../assets/SvgComponents/Warning');
const SecureLogin = () => import('../assets/SvgComponents/SecureLogin');
import { VUE_APP_VERSION_NUMBER } from '@/utilities/constants.js';

export default {
  components: {
    ForgotPasswordDialog,
    BaseMessageDialog,
    SecureLogin,
    Warning
  },

  data() {
    return {
      username: localStorage.getItem('usernameForCredentials') || '',
      password: '',
      rememberMe: false,
      loading: false,
      version: '',
      showForgotDialog: false,
      forgotLoading: false,
      displayMessageDialog: false,
      usernameForgotEmail: '',
      blockedAccountTitle: 'accountIsBlocked',
      emailRules: [v => !!v, v => /.+@.+\..+/.test(v)],
      validEmail: false,
      displayAccountNotRegisteredDialog: false,
      display3attemptsDialog: false,
      attemptsLeft: 0,
      accountNotFullyRegistered: false
    };
  },

  created() {
    this.version = VUE_APP_VERSION_NUMBER;
    const languageOptions = ['en', 'it', 'es'];
    if (languageOptions.indexOf(this.$route?.query?.lang) > -1) {
      localStorage.setItem('langOption', this.$route.query.lang);
      const payload = {
        code: this.$route.query.lang
      };

      this.setDefaultLanguage(this.$route.query.lang);
      this.getTranslatedAppLabels(payload);
    }
  },

  mounted() {
    if (this.$route?.query?.authError) {
      Swal.fire({
        title: this.$t('pleaseLogin'),
        text: this.$t('encounteredAuthError'),
        type: 'error',
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timer: 3000
      });
    }
  },

  methods: {
    ...mapActions([
      'login',
      'getUserProfile',
      'getTranslatedAppLabels',
      'startPasswordReset',
      'setDefaultLanguage'
    ]),

    async loginHandler() {
      const payload = {
        username: this.username.trim(),
        password: this.password.trim()
      };

      this.loading = true;

      const loginRes = await this.login(payload);
      if (!this.authError) {
        if (this.rememberMe) this.cacheUsername();
        const userRes = await this.getUserProfile();
        if (userRes?.status === 200) {
          const {
            routeTo,
            directRegistration
          } = this.$router.currentRoute.query;

          const preferredLanguage = userRes.data.user.profile.defaultLanguage;
          localStorage.setItem('langOption', preferredLanguage);
          this.setDefaultLanguage(preferredLanguage);

          const translationsPayload = {
            code: preferredLanguage
          };

          this.getTranslatedAppLabels(translationsPayload);

          this.loading = false;
          const voidRouteTo = ['/login', '/404'];
          if (routeTo && directRegistration) {
            this.$router.push(`${routeTo}?directRegistration="true"`);
          } else if (
            routeTo &&
            !voidRouteTo.includes(routeTo) &&
            !directRegistration
          ) {
            this.$router.push(routeTo);
          } else this.$router.push('/dashboard');
        }
      } else {
        this.loading = false;
        const { status, data, statusCode, message } = loginRes;
        if (statusCode === 400 || statusCode === 404) {
          //not a registered email
          let msg = this.$t('emailNotRegisteredPleaseSignUp');
          // incorrect password
          if (status === 'bad credentials') msg = this.$t('incorrectPassword');
          if (status === 'bad request') msg = this.$t('invalidEmailFormat');

          Swal.fire({
            title: this.$t('loginFailed'),
            text: msg,
            type: 'error',
            showConfirmButton: false,
            toast: true,
            position: 'top-end',
            timer: 5000
          });

          if (data.passwordCounter >= 7) {
            this.attemptsLeft = 10 - data.passwordCounter;
            this.display3attemptsDialog = true;
          }
        } else if (statusCode === 401) {
          //if account has not been completed
          if (message === 'Account registration not completed') {
            this.blockedAccountTitle = this.$t('accRegistrationNotCompleted');
            this.accountNotFullyRegistered = true;
          } else {
            this.blockedAccountTitle = this.$t('accountIsBlocked');
          }
          //Blocked Account
          this.displayMessageDialog = true;
        } else if (statusCode === 403) {
          //10th unsuccessful attempt
          this.blockedAccountTitle = 'tooManyFailedAttempts';
          this.displayMessageDialog = true;
        }
      }
    },

    toggleRememberMe(e) {
      if (e === null) {
        localStorage.removeItem('usernameForCredentials');
        this.rememberMe = false;
      } else this.rememberMe = true;
    },

    cacheUsername() {
      localStorage.setItem('usernameForCredentials', this.username);
    },

    sendAppropriateEmailHandler(username) {
      if (this.accountNotFullyRegistered)
        return this.accountNotFullyRegisteredHandler(username);
      return this.forgotPasswordHandler(username);
    },

    async accountNotFullyRegisteredHandler(username) {
      this.forgotLoading = true;
      try {
        const res = await account.resendConfirmationEmail(
          username,
          this.defaultLanguage
        );
      } catch (e) {
        console.log(e);
      }
      this.forgotLoading = false;
      this.displayMessageDialog = false;
      this.accountNotFullyRegistered = false;
      this.usernameForgotEmail = '';
    },

    async forgotPasswordHandler(username) {
      this.forgotLoading = true;
      try {
        const payload = {
          username: username
        };

        const res = await this.startPasswordReset(payload);
        const { message, statusCode } = res.data;

        let title = this.$t('success');
        let text = this.$t('resetPassEmailSent');
        let type = 'success';

        if (
          statusCode === 403 &&
          message === 'Account registration not completed'
        ) {
          title = this.$t('error');
          text = this.$t('accountNotFullyRegistered');
          type = 'error';
          this.displayAccountNotRegisteredDialog = true;
        }

        Swal.fire({
          title: title,
          text: text,
          type: type,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          timer: 3000
        });
      } catch (e) {
        console.log(e);
        Swal.fire({
          title: this.$t('error'),
          text: `${this.$t('unableToSendRestPass')}`,
          type: 'error',
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          timer: 3000
        });
      }

      this.forgotLoading = false;

      this.showForgotDialog = false;
      this.displayMessageDialog = false;
      this.usernameForgotEmail = '';
    },

    checkEmailRules() {
      const anyErrors = this.emailRules.some(rule => {
        return !rule(this.usernameForgotEmail);
      });

      if (anyErrors) this.validEmail = false;
      else this.validEmail = true;
    }
  },

  computed: {
    ...mapState({
      defaultLanguage: state => state.defaultLanguage,
      appLabels: state => state.languages.appLabels,
      brand: state => state.brand.brand,
      authError: state => state.authentication.authError,
      cookiePolicyURL: state => state.policies.cookiePolicyURL,
      privacyPolicyURL: state => state.policies.privacyPolicyURL
    }),

    requestedRememberMePreviously() {
      const usernameForCredentials = localStorage.getItem(
        'usernameForCredentials'
      );

      return !!usernameForCredentials;
    },

    displaySignUpLink() {
      if (!this.brand?.allowSignup) return false;

      return this.brand.allowSignup;
    },

    brandName() {
      if (this.brand?.name === 'HackCultura') return this.brand.name;

      return 'TRusT';
    }
  }
};
</script>

<style scoped>
.splash-img {
  display: none;
}
@media screen and (min-width: 650px) {
  .splash-img {
    display: block;
  }
}
</style>
